import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MapPin, CheckCircle2, Briefcase, Home as HomeIcon, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { api, friendlyError } from '../lib/api';

const PER_PAGE = 24;

export default function AgentDirectory() {
  const [sp, setSp] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState(sp.get('q') || '');
  const [city, setCity] = useState(sp.get('city') || '');
  const [specialty, setSpecialty] = useState(sp.get('specialty') || '');
  const [verified, setVerified] = useState(sp.get('verified') === 'true');

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const data = await api.listAgents({
        q: sp.get('q'),
        city: sp.get('city'),
        specialty: sp.get('specialty'),
        verified: sp.get('verified') === 'true' ? true : undefined,
        limit: PER_PAGE,
      });
      setAgents(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(friendlyError(e, 'No pudimos cargar los asesores.'));
    } finally {
      setLoading(false);
    }
  }, [sp]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const apply = () => {
    const next = new URLSearchParams();
    if (q.trim()) next.set('q', q.trim());
    if (city.trim()) next.set('city', city.trim());
    if (specialty.trim()) next.set('specialty', specialty.trim());
    if (verified) next.set('verified', 'true');
    setSp(next);
  };

  const clearAll = () => { setQ(''); setCity(''); setSpecialty(''); setVerified(false); setSp(new URLSearchParams()); };
  const hasFilters = !!(sp.get('q') || sp.get('city') || sp.get('specialty') || sp.get('verified'));

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO
        title="Asesores inmobiliarios verificados en Colombia · habita.co"
        description="Encuentra al asesor inmobiliario adecuado en Colombia. Filtra por ciudad, especialidad e inmobiliaria. Profesionales verificados con propiedades activas en habita.co."
      />
      <Header />

      <section className="bg-habita-dark text-white py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <h1 className="font-serif text-3xl sm:text-5xl font-bold">Asesores inmobiliarios en Colombia</h1>
          <p className="text-white/80 mt-3 max-w-2xl">Profesionales del sector con perfiles verificados, propiedades activas y trayectoria comprobada. Encuentra al asesor adecuado para tu próxima compra o arriendo.</p>

          <form
            onSubmit={(e) => { e.preventDefault(); apply(); }}
            className="mt-6 bg-white text-habita-dark rounded-2xl p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
            data-testid="agent-directory-filters"
          >
            <div className="md:col-span-4 flex items-center gap-2 px-3 min-w-0">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Nombre del asesor..."
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agent-directory-q"
              />
            </div>
            <div className="md:col-span-3 flex items-center gap-2 px-3 md:border-l border-gray-200 min-w-0">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={city}
                onChange={e => setCity(e.target.value)}
                placeholder="Ciudad"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agent-directory-city"
              />
            </div>
            <div className="md:col-span-3 flex items-center gap-2 px-3 md:border-l border-gray-200 min-w-0">
              <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={specialty}
                onChange={e => setSpecialty(e.target.value)}
                placeholder="Especialidad"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agent-directory-specialty"
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-2 px-2">
              <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none" data-testid="agent-directory-verified-label">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={e => setVerified(e.target.checked)}
                  className="rounded border-gray-300 text-habita focus:ring-habita"
                  data-testid="agent-directory-verified"
                />
                Verificados
              </label>
              <Button type="submit" className="btn-habita rounded-full px-5 h-10" data-testid="agent-directory-apply">Buscar</Button>
            </div>
          </form>

          {hasFilters && (
            <button onClick={clearAll} className="text-xs text-white/70 hover:text-white mt-3 inline-flex items-center gap-1" data-testid="agent-directory-clear">
              <X className="w-3 h-3" /> Limpiar filtros
            </button>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <p className="text-sm text-gray-600 mb-5" data-testid="agent-directory-count">
          {loading ? 'Cargando…' : `${total} ${total === 1 ? 'asesor' : 'asesores'}`}
        </p>

        {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm" data-testid="agent-directory-error">{err}</div>}

        {!loading && agents.length === 0 && !err && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center" data-testid="agent-directory-empty">
            <p className="font-serif text-xl font-bold text-habita-dark">Aún no hay asesores que coincidan</p>
            <p className="text-sm text-gray-600 mt-2">Prueba ajustando los filtros o explora nuestras propiedades destacadas.</p>
            <Link to="/buscar" className="inline-block mt-4 text-sm font-semibold text-habita hover:underline">Ver propiedades →</Link>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {agents.map(a => <AgentCard key={a.id} a={a} />)}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function AgentCard({ a }) {
  return (
    <Link
      to={`/agente/${a.slug}`}
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-habita transition-all group"
      data-testid={`agent-card-${a.id}`}
    >
      <div className="h-24 bg-habita-mint relative">
        {a.cover_url
          ? <img src={a.cover_url} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-habita to-emerald-600" />}
      </div>
      <div className="px-5 pb-5 -mt-10 relative">
        <div className="w-16 h-16 rounded-full bg-white border-4 border-white shadow-md overflow-hidden">
          {a.avatar
            ? <img src={a.avatar} alt={a.name} className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-habita-mint flex items-center justify-center text-habita text-xl font-bold">{a.name?.charAt(0)}</div>}
        </div>
        <div className="mt-2.5 flex items-start justify-between gap-2">
          <p className="font-serif font-bold text-habita-dark text-base group-hover:text-habita transition-colors flex-1 min-w-0 truncate">{a.name}</p>
          {a.owner_type === 'propietario' ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold shrink-0" data-testid={`agent-card-owner-${a.id}`}>
              PROPIETARIO
            </span>
          ) : a.verified && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold shrink-0" data-testid={`agent-card-verified-${a.id}`}>
              <CheckCircle2 className="w-2.5 h-2.5" /> VERIF.
            </span>
          )}
        </div>
        {a.title && <p className="text-xs text-gray-600 mt-0.5 truncate">{a.title}</p>}
        <div className="text-[11px] text-gray-500 mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {a.city && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {a.city}</span>}
          {a.years_experience != null && <span>· {a.years_experience} años</span>}
          <span className="flex items-center gap-0.5"><HomeIcon className="w-3 h-3" /> {a.active_properties}</span>
        </div>
        {a.specialties?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2.5">
            {a.specialties.slice(0, 3).map(s => (
              <span key={s} className="px-2 py-0.5 rounded-full bg-habita-mint text-habita text-[10px] font-semibold">{s}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
